import axios from 'axios';
import { getApiUrl } from './api';

// Tous les boutons d'import : dès qu'une requête axios porte un FormData avec ≥ 900 Ko de fichiers, le fichier part
// en morceaux de 1 Mo (POST /api/upload/{id}/chunk) puis le serveur rejoue la requête d'origine en interne
// (POST /api/upload/forward). Le proxy de production coupait les envois de plusieurs Mo d'un bloc (Cloudflare 520).
const CHUNK = 1024 * 1024;
const MIN = 900 * 1024;
const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const apiRoot = () => getApiUrl().replace(/\/api\/?$/, ''); // '' sur un domaine client (URL relative), sinon l'origine du backend
const apiOrigin = () => { const r = apiRoot(); try { return r ? new URL(r).origin : window.location.origin; } catch { return window.location.origin; } };

const targetOf = (config) => {
  const url = (config.baseURL && !/^https?:/.test(config.url || '') ? config.baseURL.replace(/\/$/, '') + '/' : '') + (config.url || '');
  let path;
  try { path = /^https?:/.test(url) ? new URL(url).pathname + new URL(url).search : url; } catch { return null; }
  if (!/^https?:/.test(url) && !path.startsWith('/')) return null;
  const idx = path.indexOf('/api/');
  if (idx < 0) return null;
  path = path.slice(idx);
  if (config.params) { const sp = new URLSearchParams(config.params); path += (path.includes('?') ? '&' : '?') + sp.toString(); }
  if (path.startsWith('/api/chunked-upload/') || /\/upload\/[A-Za-z0-9]+\/chunk/.test(path)) return null; // jamais re-découper un morceau
  const host = /^https?:/.test(url) ? new URL(url).origin : window.location.origin;
  if (host !== apiOrigin() && host !== window.location.origin) return null;
  return path;
};

const sizeOf = (fd) => { let n = 0; for (const [, v] of fd.entries()) if (v instanceof Blob) n += v.size; return n; };

async function sendChunks(file, onProgress) {
  const id = uid(); const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append('index', String(i)); fd.append('total', String(total));
    fd.append('chunk', file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK)), `part-${i}`);
    let tries = 0;
    for (;;) {
      try { await axios.post(`${getApiUrl()}/chunked-upload/${id}/chunk`, fd, { withCredentials: true, timeout: 60000, __chunked: true }); break; }
      catch (e) { if (++tries >= 3 || [401, 403, 413, 422].includes(e?.response?.status)) throw e; await new Promise((r) => setTimeout(r, 800 * tries)); }
    }
    onProgress?.((i + 1) / total);
  }
  return id;
}

async function chunkedAdapter(config) {
  const fd = config.data; const target = targetOf(config);
  const fields = []; const files = [];
  let done = 0; const totalBytes = sizeOf(fd);
  for (const [k, v] of fd.entries()) {
    if (v instanceof Blob) {
      const upload_id = await sendChunks(v, (p) => { config.onUploadProgress?.({ loaded: done + p * v.size, total: totalBytes, lengthComputable: true }); });
      done += v.size;
      files.push({ field: k, upload_id, filename: v.name || 'fichier', type: v.type || 'application/octet-stream' });
    } else fields.push([k, String(v)]);
  }
  const res = await axios.post(`${getApiUrl()}/chunked-upload/forward`, { target, fields, files }, { withCredentials: true, timeout: 0, __chunked: true, validateStatus: () => true, responseType: config.responseType === 'blob' ? 'blob' : 'json' });
  const out = { data: res.data, status: res.status, statusText: res.statusText, headers: res.headers, config, request: res.request };
  if (res.status >= 400) { const err = new axios.AxiosError(res.data?.detail || `Request failed with status code ${res.status}`, String(res.status), config, res.request, out); throw err; }
  return out;
}

export function installChunkedUploads() {
  axios.interceptors.request.use((config) => {
    if (config.__chunked || !(config.data instanceof FormData)) return config;
    if (sizeOf(config.data) < MIN) return config;
    if (!targetOf(config)) return config;
    return { ...config, adapter: chunkedAdapter };
  });
}
